import React, { useState, useEffect } from "react";
import { Button, Select, Spin, message } from "antd";
import { createGrievanceCategory, getAllGrievanceCategories, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const GrievanceCategoryListComponent = ({ exception, onClose, onResolved }) => {
    const [grievanceCategoryName, setGrievanceCategoryName] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [grievanceCategories, setGrievanceCategories] = useState([]);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const extractGrievanceCategoryDetails = () => {
            setGrievanceCategoryName(exception?.unknown_infomation);
        };

        if (exception && exception.description) {
            extractGrievanceCategoryDetails();
        }

        const fetchGrievanceCategories = async () => {
            setLoading(true);
            try {
                const response = await getAllGrievanceCategories();
                const categoriesData = response?.data?.data || [];
                setGrievanceCategories(Array.isArray(categoriesData) ? categoriesData : []);
            } catch (error) {
                console.error('Failed to fetch grievance categories', error);
                message.error('Failed to fetch grievance categories');
            } finally {
                setLoading(false);
            }
        };

        fetchGrievanceCategories();
    }, [exception]);

    const handleMarkAsNew = async () => {
        setLoading(true);
        if (grievanceCategoryName) {
            if (exception.status === 'pending') {
                try {
                    const payload = {
                        id: 0,
                        name: grievanceCategoryName,
                        alias_for: null,
                        created_at: new Date().toISOString(),
                        updated_at: new Date().toISOString()
                    };
                    
                    await createGrievanceCategory(payload);
                    await updateException(exception.id);
                    console.log(`Grievance category "${grievanceCategoryName}" created successfully.`);
                    message.success('Grievance category created');
                    try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                    onClose();
                } catch (error) {
                    console.error(`Failed to create grievance category "${grievanceCategoryName}".`, error);
                    message.error('Failed to create grievance category');
                } finally {
                    setLoading(false);
                }
            } else {
                console.warn("Exception already resolved.");
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            }
        } else {
            console.warn("Grievance category name not found.");
            setLoading(false);
            onClose();
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleGrievanceCategorySelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId) return;
        setLoading(true);
        if (exception.status === 'pending') {
            try {
                const payload = {
                    id: 0,
                    name: grievanceCategoryName,
                    alias_for: selectedAliasId,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                };
                
                await createGrievanceCategory(payload);
                await updateException(exception.id);
                console.log(`Grievance category "${grievanceCategoryName}" set as alias for category with ID: ${selectedAliasId}`);
                message.success('Alias saved');
                try { if (typeof onResolved === 'function') await onResolved(); } catch {}
                onClose();
            } catch (error) {
                console.error(`Failed to update alias for grievance category with ID: ${selectedAliasId}`, error);
                message.error('Failed to save alias');
            } finally {
                setLoading(false);
            }
        } else {
            console.warn("Exception already resolved.");
            try { if (typeof onResolved === 'function') await onResolved(); } catch {}
            onClose();
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button 
                            type="primary" 
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button 
                            type="default" 
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a grievance category"
                                    onChange={handleGrievanceCategorySelect}
                                    disabled={loading}
                                    value={selectedAliasId ?? undefined}
                                >
                                    {grievanceCategories.map((category) => (
                                        <Option key={category.id} value={category.id}>
                                            {category.name}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default GrievanceCategoryListComponent;
